import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { dateStr, inr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Building2, Wallet, TrendingDown, Plus, Receipt, Image as ImageIcon, IndianRupee, FileDown, Upload, CheckCircle2, AlertTriangle, ListTree, Trash2 } from "lucide-react";
import { toast } from "sonner";

function BillLink({ path }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/files/${path}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
    } catch { toast.error("Bill not available"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={open} disabled={busy} className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
      <ImageIcon className="h-3 w-3" /> {busy ? "…" : "View"}
    </button>
  );
}

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const loadVendors = async () => {
    const r = await api.get("/vendors");
    setVendors(r.data);
    if (r.data.length && !selected) setSelected(r.data[0].vendor);
  };
  const loadLedger = async (v) => {
    if (!v) return;
    const r = await api.get(`/vendors/ledger?vendor=${encodeURIComponent(v)}`);
    setLedger(r.data);
  };

  useEffect(() => { loadVendors(); }, []);
  useEffect(() => { loadLedger(selected); }, [selected]);

  const totalBilled = vendors.reduce((s, v) => s + Number(v.billed || 0), 0);
  const totalPaid = vendors.reduce((s, v) => s + Number(v.paid || 0), 0);
  const totalOut = vendors.reduce((s, v) => s + Number(v.outstanding || 0), 0);

  return (
    <div className="space-y-6" data-testid="vendors-root">
      <SectionHeader
        title="Vendor Ledger"
        subtitle="Every bill you've raised against each supplier, across every project, with a live outstanding snapshot"
      />

      <div className="flex justify-end">
        <BankReconcileDialog
          vendorHint={selected || ""}
          onCommitted={() => { loadVendors(); loadLedger(selected); }}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="v-count" label="Vendors on file" value={vendors.length} icon={Building2} />
        <KPI testId="v-billed" label="Total billed (all vendors)" value={totalBilled} compact icon={Receipt} />
        <KPI testId="v-paid" label="Total paid" value={totalPaid} compact icon={IndianRupee} tone="good" />
        <KPI testId="v-out" label="Outstanding to vendors" value={totalOut} compact icon={TrendingDown} tone={totalOut > 0 ? "warn" : "good"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Vendor list */}
        <Card className="shadow-none md:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-display">Vendors</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 max-h-[70vh] overflow-y-auto">
            {vendors.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">No vendors yet</div>
            )}
            <div className="divide-y">
              {vendors.map((v) => {
                const isActive = v.vendor === selected;
                return (
                  <button
                    key={v.vendor}
                    data-testid={`vendor-item-${v.vendor}`}
                    onClick={() => setSelected(v.vendor)}
                    className={`w-full text-left py-2.5 px-2 rounded transition-colors ${isActive ? "bg-primary/10" : "hover:bg-accent/60"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{v.vendor}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {v.bills} bills · last {dateStr(v.last_date)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-medium">{inr(v.billed, { compact: true })}</div>
                        <div className={`text-[10px] font-mono ${v.outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                          {v.outstanding > 0 ? `Due ${inr(v.outstanding, { compact: true })}` : "Cleared"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Ledger */}
        <Card className="shadow-none md:col-span-2">
          <CardHeader className="pb-2 flex-row items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-sm font-semibold font-display truncate">
                {selected || "Select a vendor"}
              </CardTitle>
              {ledger && (
                <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                  {ledger.bill_count} bills · {ledger.payment_count} payments
                </div>
              )}
            </div>
            {selected && (
              <div className="flex items-center gap-2">
                <StatementButton vendor={selected} />
                <RecordPaymentDialog
                  vendor={selected}
                  open={payOpen}
                  onOpenChange={setPayOpen}
                  onSaved={() => { loadVendors(); loadLedger(selected); }}
                />
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            {!ledger ? (
              <div className="text-sm text-muted-foreground text-center py-8">Loading ledger…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="rounded-md border p-2.5 bg-muted/30">
                    <div className="text-[10px] uppercase font-mono text-muted-foreground">Billed</div>
                    <div className="text-sm font-semibold" data-testid="ledger-billed">{inr(ledger.total_billed, { compact: true })}</div>
                  </div>
                  <div className="rounded-md border p-2.5 bg-muted/30">
                    <div className="text-[10px] uppercase font-mono text-muted-foreground">Paid</div>
                    <div className="text-sm font-semibold text-emerald-700" data-testid="ledger-paid">{inr(ledger.total_paid, { compact: true })}</div>
                  </div>
                  <div className={`rounded-md border p-2.5 ${ledger.outstanding > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                    <div className="text-[10px] uppercase font-mono text-muted-foreground">Outstanding</div>
                    <div className={`text-sm font-semibold ${ledger.outstanding > 0 ? "text-amber-800" : "text-emerald-700"}`} data-testid="ledger-outstanding">
                      {inr(ledger.outstanding, { compact: true })}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                        <th className="py-2 font-medium">Date</th>
                        <th className="py-2 font-medium">Kind</th>
                        <th className="py-2 font-medium">Ref / Description</th>
                        <th className="py-2 font-medium">Project</th>
                        <th className="py-2 font-medium text-right">Debit (bill)</th>
                        <th className="py-2 font-medium text-right">Credit (paid)</th>
                        <th className="py-2 font-medium text-right">Balance</th>
                        <th className="py-2 font-medium">Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger.events || []).map((ev, i) => (
                        <tr key={i} className="border-b hover:bg-accent/40" data-testid={`ledger-row-${i}`}>
                          <td className="py-2 text-xs">{dateStr(ev.date)}</td>
                          <td className="py-2">
                            {ev.kind === "bill"
                              ? <Badge className="bg-amber-100 text-amber-800 border-amber-200">Bill</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Payment</Badge>}
                          </td>
                          <td className="py-2 max-w-[240px] truncate">
                            <div className="text-xs font-mono">{ev.ref || "—"}</div>
                            {ev.description && <div className="text-[10px] text-muted-foreground truncate">{ev.description}</div>}
                            {ev.mode && <div className="text-[10px] text-muted-foreground uppercase font-mono">{ev.mode}{ev.notes ? ` · ${ev.notes}` : ""}</div>}
                            {ev.wbs_label && (
                              <div className="text-[10px] font-mono text-primary/80 mt-0.5 truncate" data-testid={`ledger-wbs-${i}`}>
                                <ListTree className="h-2.5 w-2.5 inline mr-0.5" />{ev.wbs_label}
                              </div>
                            )}
                            {ev.allocations && ev.allocations.length > 0 && (
                              <div className="mt-1 space-y-0.5" data-testid={`ledger-allocations-${i}`}>
                                <div className="text-[10px] font-mono text-secondary uppercase tracking-wide">Split across {ev.allocations.length} milestones</div>
                                {ev.allocations.map((a, ai) => (
                                  <div key={ai} className="text-[10px] font-mono text-muted-foreground pl-3 flex justify-between gap-2">
                                    <span className="truncate">↳ {a.wbs_label}</span>
                                    <span className="text-emerald-700 shrink-0">{inr(a.amount, { compact: true })}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 text-xs">{ev.project || "—"}</td>
                          <td className="py-2 cell-num">
                            {ev.kind === "bill" ? inr(ev.amount, { compact: true }) : ""}
                          </td>
                          <td className="py-2 cell-num text-emerald-700">
                            {ev.kind === "payment" ? `-${inr(ev.amount, { compact: true })}` : ""}
                          </td>
                          <td className="py-2 cell-num font-medium">{inr(ev.balance, { compact: true })}</td>
                          <td className="py-2">
                            {ev.bill_image_path ? <BillLink path={ev.bill_image_path} /> : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                      {(!ledger.events || ledger.events.length === 0) && (
                        <tr><td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No transactions</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RecordPaymentDialog({ vendor, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: 0, mode: "bank", reference: "", notes: "",
    project_id: "", wbs_id: "",
  });
  const [split, setSplit] = useState(false);
  const [allocations, setAllocations] = useState([{ project_id: "", wbs_id: "", amount: 0 }]);
  const [projects, setProjects] = useState([]);
  const [wbsList, setWbsList] = useState([]);          // for single-mode
  const [wbsByProject, setWbsByProject] = useState({}); // for split-mode per-row cache

  useEffect(() => {
    if (open) api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [open]);
  useEffect(() => {
    if (!form.project_id) { setWbsList([]); setForm((f) => ({ ...f, wbs_id: "" })); return; }
    api.get(`/wbs?project_id=${form.project_id}`).then((r) => setWbsList(r.data)).catch(() => setWbsList([]));
  }, [form.project_id]);

  const wbsFor = async (pid) => {
    if (!pid) return [];
    if (wbsByProject[pid]) return wbsByProject[pid];
    try {
      const r = await api.get(`/wbs?project_id=${pid}`);
      setWbsByProject((m) => ({ ...m, [pid]: r.data }));
      return r.data;
    } catch { return []; }
  };

  const updateAlloc = (i, patch) => setAllocations(allocations.map((a, j) => j === i ? { ...a, ...patch } : a));
  const addAlloc = () => setAllocations([...allocations, { project_id: "", wbs_id: "", amount: 0 }]);
  const removeAlloc = (i) => setAllocations(allocations.filter((_, j) => j !== i));

  const allocSum = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  const total = Number(form.amount || 0);
  const diff = Number((total - allocSum).toFixed(2));

  const save = async () => {
    if (!(total > 0)) return toast.error("Enter amount");
    let payload = { vendor, ...form, amount: total };
    if (split) {
      const valid = allocations.filter((a) => a.wbs_id && Number(a.amount) > 0);
      if (!valid.length) return toast.error("Add at least one allocation");
      if (Math.abs(diff) > 0.5) return toast.error(`Allocations must sum to ${total} (off by ${diff})`);
      payload = {
        ...payload,
        project_id: "", wbs_id: "",  // multi
        allocations: valid.map((a) => ({
          project_id: a.project_id || "", wbs_id: a.wbs_id, amount: Number(a.amount),
        })),
      };
    }
    try {
      await api.post("/expense-payments", payload);
      toast.success(split ? `Payment recorded · split across ${allocations.filter((a) => a.wbs_id).length} milestones` : "Payment recorded");
      onOpenChange(false);
      setForm({ date: new Date().toISOString().slice(0, 10), amount: 0, mode: "bank",
                reference: "", notes: "", project_id: "", wbs_id: "" });
      setSplit(false); setAllocations([{ project_id: "", wbs_id: "", amount: 0 }]);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="record-vendor-pay-btn" className="h-9 bg-primary hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5 mr-1" /> Record payment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Payment to {vendor}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Amount (₹)</Label>
            <Input type="number" data-testid="pay-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["bank", "rtgs", "neft", "cheque", "cash", "upi"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Reference / UTR</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </div>
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <input type="checkbox" id="split-toggle" data-testid="pay-split-toggle"
              checked={split} onChange={(e) => setSplit(e.target.checked)} />
            <Label htmlFor="split-toggle" className="text-xs cursor-pointer select-none">
              <b>Split across milestones</b> — one bank debit spread over multiple WBS phases
            </Label>
          </div>

          {!split && (<>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Project</Label>
              <Select value={form.project_id || "__none"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none" ? "" : v, wbs_id: "" })}>
                <SelectTrigger data-testid="pay-project"><SelectValue placeholder="— untagged —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— untagged —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.code} · {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Milestone / WBS</Label>
              <Select value={form.wbs_id || "__none"}
                onValueChange={(v) => setForm({ ...form, wbs_id: v === "__none" ? "" : v })}
                disabled={!form.project_id}>
                <SelectTrigger data-testid="pay-wbs">
                  <SelectValue placeholder={form.project_id ? "— untagged —" : "Pick project first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— untagged —</SelectItem>
                  {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>)}

          {split && (
            <div className="col-span-2 border rounded-md p-2 bg-muted/20 space-y-2" data-testid="pay-allocations">
              <div className="flex items-center justify-between text-xs">
                <div className="uppercase font-mono text-muted-foreground">Allocations · sum must equal {inr(total, { compact: true })}</div>
                <Button size="sm" variant="ghost" onClick={addAlloc} data-testid="pay-alloc-add" className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Add row
                </Button>
              </div>
              {allocations.map((a, i) => (
                <AllocRow key={i} row={a} projects={projects} wbsFor={wbsFor}
                  onChange={(patch) => updateAlloc(i, patch)}
                  onRemove={() => removeAlloc(i)}
                  index={i} />
              ))}
              <div className={`flex items-center justify-between text-xs font-mono px-1 py-1 rounded ${Math.abs(diff) < 0.5 && total > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                <span>Allocated: {inr(allocSum, { compact: true })} of {inr(total, { compact: true })}</span>
                <span data-testid="pay-alloc-diff">{diff === 0 ? "✓ Balanced" : `Δ ${inr(Math.abs(diff), { compact: true })} ${diff > 0 ? "under" : "over"}`}</span>
              </div>
            </div>
          )}

          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground mb-1 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="pay-save" onClick={save} className="bg-primary hover:bg-primary/90">Save payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllocRow({ row, projects, wbsFor, onChange, onRemove, index }) {
  const [wbsOpts, setWbsOpts] = useState([]);
  useEffect(() => {
    if (!row.project_id) { setWbsOpts([]); return; }
    wbsFor(row.project_id).then(setWbsOpts);
  }, [row.project_id]);
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`pay-alloc-row-${index}`}>
      <div className="col-span-4">
        <Select value={row.project_id || "__none"} onValueChange={(v) => onChange({ project_id: v === "__none" ? "" : v, wbs_id: "" })}>
          <SelectTrigger data-testid={`pay-alloc-project-${index}`} className="h-8 text-xs"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— none —</SelectItem>
            {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.code} · {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-4">
        <Select value={row.wbs_id || "__none"} onValueChange={(v) => onChange({ wbs_id: v === "__none" ? "" : v })} disabled={!row.project_id}>
          <SelectTrigger data-testid={`pay-alloc-wbs-${index}`} className="h-8 text-xs"><SelectValue placeholder={row.project_id ? "Milestone" : "Pick project"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— none —</SelectItem>
            {wbsOpts.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-3">
        <Input type="number" placeholder="₹" data-testid={`pay-alloc-amount-${index}`}
          value={row.amount} onChange={(e) => onChange({ amount: Number(e.target.value) })}
          className="h-8 text-xs text-right" />
      </div>
      <div className="col-span-1">
        <Button size="sm" variant="ghost" onClick={onRemove} data-testid={`pay-alloc-remove-${index}`}
          className="h-8 w-8 p-0 text-destructive"><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

function StatementButton({ vendor }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 60 * 86400 * 1000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: monthAgo, to: today });

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("session_token");
      const url = `${API}/vendors/statement?vendor=${encodeURIComponent(vendor)}&from_date=${range.from}&to_date=${range.to}`;
      const r = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Statement_${vendor.replace(/\s+/g, "_")}_${range.from}_${range.to}.pdf`;
      link.click();
      toast.success("Statement downloaded");
      setOpen(false);
    } catch { toast.error("Failed to generate statement"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="vendor-statement-btn" className="h-9 border-primary/40">
          <FileDown className="h-3.5 w-3.5 mr-1" /> Statement PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Statement for {vendor}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">From</Label>
            <Input data-testid="stmt-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">To</Label>
            <Input data-testid="stmt-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
          <div className="col-span-2 text-[11px] text-muted-foreground">
            Emailable A4 PDF with opening balance, bills · payments timeline, closing balance and aging (0-30 / 31-60 / 61-90 / 90+).
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="stmt-download" onClick={download} disabled={busy} className="bg-primary hover:bg-primary/90">
            {busy ? "Generating…" : "Download PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CONFIDENCE_META = {
  high:               { cls: "bg-emerald-100 text-emerald-800 border-emerald-200",  label: "High"    },
  medium:             { cls: "bg-sky-100 text-sky-800 border-sky-200",              label: "Medium"  },
  manual:             { cls: "bg-slate-100 text-slate-800 border-slate-200",        label: "Manual"  },
  unmatched:          { cls: "bg-red-100 text-red-800 border-red-200",              label: "Unmatched"},
  "already-recorded": { cls: "bg-amber-100 text-amber-800 border-amber-200",        label: "Already recorded" },
};

function BankReconcileDialog({ vendorHint, onCommitted }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({}); // idx -> bool

  const reset = () => { setCsv(""); setPreview(null); setSelected({}); setBusy(false); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) return toast.error("Upload a .csv file");
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!csv.trim()) return toast.error("Paste CSV or upload a file");
    setBusy(true);
    try {
      const r = await api.post("/reconcile/bank/preview", { csv_text: csv, vendor_hint: vendorHint });
      setPreview(r.data);
      // Auto-select all matched-and-not-already-recorded rows
      const sel = {};
      r.data.rows.forEach((row, i) => {
        if (row.suggested_vendor && !row.already_recorded) sel[i] = true;
      });
      setSelected(sel);
      toast.success(`Parsed ${r.data.parsed} txns · ${r.data.matched} auto-matched`);
    } catch (e) { toast.error("Preview failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview) return;
    const rows = preview.rows
      .map((r, i) => ({ ...r, __i: i }))
      .filter((r) => selected[r.__i] && r.suggested_vendor && !r.already_recorded)
      .map((r) => ({
        vendor: r.suggested_vendor, date: r.date, amount: r.amount,
        mode: "bank", reference: r.reference, notes: r.description,
      }));
    if (!rows.length) return toast.error("Nothing selected");
    setBusy(true);
    try {
      const res = await api.post("/reconcile/bank/commit", { rows });
      toast.success(`Committed ${res.data.saved} payments${res.data.skipped ? ` · ${res.data.skipped} skipped` : ""}`);
      setOpen(false); reset(); onCommitted();
    } catch { toast.error("Commit failed"); }
    finally { setBusy(false); }
  };

  const updateVendor = (i, v) => {
    if (!preview) return;
    const rows = preview.rows.map((row, j) => j === i ? { ...row, suggested_vendor: v, confidence: v ? "manual" : "unmatched" } : row);
    setPreview({ ...preview, rows });
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="bank-reconcile-btn" className="border-secondary/50">
          <Upload className="h-3.5 w-3.5 mr-1 text-secondary" /> Import bank CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-secondary" /> Bank statement reconcile
          </DialogTitle>
          <DialogDescription>
            Upload a bank CSV — we auto-detect date, narration, amount and reference; match each debit to a vendor by name and existing payments;
            you approve, then we create the corresponding vendor payments in the ledger.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Upload CSV</Label>
              <input type="file" accept=".csv,.txt" onChange={onFile} data-testid="bank-file"
                className="block w-full text-xs border rounded-md p-2 file:mr-2 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">…or paste CSV rows here</Label>
              <Textarea rows={7} data-testid="bank-csv-text" value={csv} onChange={(e) => setCsv(e.target.value)}
                placeholder={`Date,Narration,Debit,Credit,Ref\n2026-07-15,DOW CORNING PAYMENT UTR12345,850000,,UTR12345\n2026-07-20,SIKA SILICONE INV RTGS,500000,,RTGS999`}
                className="font-mono text-xs" />
            </div>
            <div className="text-right">
              <Button data-testid="bank-preview" onClick={runPreview} disabled={busy} className="bg-primary hover:bg-primary/90">
                {busy ? "Parsing…" : "Preview matches"}
              </Button>
            </div>
          </div>
        )}

        {preview && (
          <div className="flex-1 overflow-hidden flex flex-col space-y-2">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="rounded-md border p-2 bg-muted/30" data-testid="bank-parsed">
                <div className="text-[10px] uppercase font-mono text-muted-foreground">Parsed</div>
                <div className="font-semibold">{preview.parsed}</div>
              </div>
              <div className="rounded-md border p-2 bg-emerald-50 border-emerald-200" data-testid="bank-matched">
                <div className="text-[10px] uppercase font-mono text-emerald-700">Auto-matched</div>
                <div className="font-semibold text-emerald-800">{preview.matched}</div>
              </div>
              <div className="rounded-md border p-2 bg-amber-50 border-amber-200" data-testid="bank-existing">
                <div className="text-[10px] uppercase font-mono text-amber-800">Already recorded</div>
                <div className="font-semibold text-amber-900">{preview.already_recorded}</div>
              </div>
              <div className="rounded-md border p-2 bg-red-50 border-red-200" data-testid="bank-unmatched">
                <div className="text-[10px] uppercase font-mono text-red-800">Unmatched</div>
                <div className="font-semibold text-red-900">{preview.unmatched}</div>
              </div>
            </div>
            <div className="overflow-auto flex-1 border rounded-md">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card z-10 border-b">
                  <tr className="text-left text-[10px] uppercase font-mono text-muted-foreground">
                    <th className="py-2 px-2 w-8"></th>
                    <th className="py-2 px-2">Date</th>
                    <th className="py-2 px-2">Description</th>
                    <th className="py-2 px-2">Ref</th>
                    <th className="py-2 px-2 text-right">Amount</th>
                    <th className="py-2 px-2">Suggested vendor</th>
                    <th className="py-2 px-2">Confidence</th>
                    <th className="py-2 px-2 text-right">Vendor outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const conf = CONFIDENCE_META[r.already_recorded ? "already-recorded" : (r.confidence || "unmatched")] || CONFIDENCE_META.unmatched;
                    const disabled = r.already_recorded || !r.suggested_vendor;
                    return (
                      <tr key={i} className="border-b hover:bg-accent/40" data-testid={`bank-row-${i}`}>
                        <td className="py-1.5 px-2">
                          <input type="checkbox" data-testid={`bank-select-${i}`}
                            disabled={disabled}
                            checked={!!selected[i]}
                            onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })} />
                        </td>
                        <td className="py-1.5 px-2 font-mono text-[10px]">{r.date}</td>
                        <td className="py-1.5 px-2 max-w-[220px] truncate">{r.description}</td>
                        <td className="py-1.5 px-2 font-mono text-[10px]">{r.reference}</td>
                        <td className="py-1.5 px-2 cell-num font-medium">{inr(r.amount, { compact: true })}</td>
                        <td className="py-1.5 px-2 min-w-[180px]">
                          <Select value={r.suggested_vendor || "__none"} onValueChange={(v) => updateVendor(i, v === "__none" ? "" : v)}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`bank-vendor-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">— none —</SelectItem>
                              {(preview.vendors || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1.5 px-2">
                          <Badge className={conf.cls}>{conf.label}</Badge>
                        </td>
                        <td className="py-1.5 px-2 cell-num text-[11px] text-muted-foreground">
                          {r.vendor_outstanding !== null && r.vendor_outstanding !== undefined ? inr(r.vendor_outstanding, { compact: true }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground font-mono">{selectedCount} selected · will create {selectedCount} vendor payments</div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={reset}>Start over</Button>
                <Button size="sm" onClick={commit} disabled={busy || !selectedCount} data-testid="bank-commit"
                  className="bg-primary hover:bg-primary/90">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  {busy ? "Committing…" : `Commit ${selectedCount} payments`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
